// Electron Bridge - Provides a unified interface for Electron and browser environments
// Enhanced with comprehensive environment detection and feature flags

// Cache for environment detection result
let environmentCache = null;

// Comprehensive environment detection with multiple validation methods
const detectEnvironment = () => {
  if (environmentCache !== null) {
    return environmentCache;
  }

  const hasWindow = typeof window !== 'undefined';
  const hasProcess = hasWindow && window.process && typeof window.process === 'object';
  const hasProcessType = hasProcess && typeof window.process.type === 'string';
  const hasRequire = hasWindow && typeof window.require === 'function';
  const hasElectronInUserAgent = hasWindow && window.navigator && 
    window.navigator.userAgent && window.navigator.userAgent.includes('Electron');
  
  // Check for Electron-specific globals
  const hasElectronGlobals = hasWindow && (
    window.electronAPI || 
    window.sqliteAPI || 
    window.sqliteUtils ||
    (hasRequire && window.require.resolve)
  );

  // Primary Electron detection
  const isElectron = !!(hasProcessType && hasRequire);
  
  // Secondary validation
  const isDefinitelyElectron = isElectron && (hasElectronInUserAgent || hasElectronGlobals);
  
  // Detect specific capabilities
  const capabilities = {
    isElectron: isDefinitelyElectron,
    hasNodeIntegration: isDefinitelyElectron && hasRequire,
    hasIPC: false,
    hasSQLite: !!(hasWindow && window.sqliteAPI),
    hasFileSystem: false,
    hasShell: false,
    platform: hasProcess ? window.process.platform : 'web',
    isWeb: !isDefinitelyElectron,
    isDevelopment: hasProcess && window.process.env && window.process.env.NODE_ENV === 'development',
  };

  // Try to detect IPC and other Electron features
  if (isDefinitelyElectron && hasRequire) {
    try {
      const electron = window.require('electron');
      capabilities.hasIPC = !!(electron && electron.ipcRenderer);
      capabilities.hasShell = !!(electron && electron.shell);
      capabilities.hasFileSystem = !!(window.require('fs'));
    } catch (error) {
      console.warn('Failed to detect Electron features:', error);
    }
  }

  // Feature flags based on capabilities
  const features = {
    fileOperations: capabilities.hasFileSystem && capabilities.hasIPC,
    directorySelection: capabilities.hasIPC && capabilities.isElectron,
    sqliteDatabase: capabilities.hasSQLite,
    comfyUIIntegration: capabilities.hasIPC && capabilities.isElectron,
    nativeAuth: capabilities.hasIPC && capabilities.isElectron,
    localFileUpload: capabilities.hasFileSystem && capabilities.hasIPC,
    imageMetadataEdit: capabilities.hasIPC && capabilities.isElectron,
    shellOperations: capabilities.hasShell,
    logging: capabilities.hasIPC || capabilities.isWeb, // Can log in both environments
  };

  environmentCache = {
    ...capabilities,
    features
  };

  return environmentCache;
};

// Get environment info
const getEnvironment = () => detectEnvironment();

// Check if we're running in Electron (legacy function for backward compatibility)
const isElectron = () => getEnvironment().isElectron;

// Create a mock IPC renderer for browser environments
const createMockIpcRenderer = () => {
  const mockHandlers = new Map();
  
  return {
    send: (channel, ...args) => {
      console.log(`[Browser Mock] IPC send to channel: ${channel}`, ...args);
      
      // Handle specific channels that need mock responses
      switch (channel) {
        case 'init-ini':
          console.log('[Browser Mock] Initializing configuration (mock)');
          break;
        case 'pwa-log':
          console.log('[Browser Mock] Log:', ...args);
          break;
        case 'openDirectory':
          console.warn('[Browser Mock] Directory selection not available in browser');
          break;
        case 'update-image':
        case 'delete-image':
          console.warn('[Browser Mock] File operations not available in browser');
          break;
        case 'check-comfyui':
          console.warn('[Browser Mock] ComfyUI integration not available in browser');
          break;
        case 'google_auth':
          console.warn('[Browser Mock] Native auth not available in browser - use web OAuth flow');
          break;
      }
    },
    
    on: (channel, callback) => {
      console.log(`[Browser Mock] IPC listener registered for channel: ${channel}`);
      
      // Store handlers for potential mock triggers
      if (!mockHandlers.has(channel)) {
        mockHandlers.set(channel, []);
      }
      mockHandlers.get(channel).push(callback);
      
      // Mock some essential responses
      if (channel === 'init-ini-reply') {
        setTimeout(() => {
          callback(null, { status: 'success', mock: true });
        }, 100);
      }
      
      return this;
    },
    
    once: (channel, callback) => {
      console.log(`[Browser Mock] IPC once listener registered for channel: ${channel}`);
      
      const onceWrapper = (...args) => {
        callback(...args);
        this.removeListener(channel, onceWrapper);
      };
      
      return this.on(channel, onceWrapper);
    },
    
    removeListener: (channel, callback) => {
      console.log(`[Browser Mock] IPC listener removed for channel: ${channel}`);
      
      if (mockHandlers.has(channel)) {
        const handlers = mockHandlers.get(channel);
        const index = handlers.indexOf(callback);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
      
      return this;
    },
    
    removeAllListeners: (channel) => {
      console.log(`[Browser Mock] All IPC listeners removed for channel: ${channel}`);
      
      if (channel) {
        mockHandlers.delete(channel);
      } else {
        mockHandlers.clear();
      }
      
      return this;
    },
    
    // Alias for removeListener (Electron IPC compatibility)
    off: function(channel, callback) {
      return this.removeListener(channel, callback);
    },
    
    invoke: async (channel, ...args) => {
      console.log(`[Browser Mock] IPC invoke to channel: ${channel}`, ...args);
      
      // Mock responses for invoke calls
      switch (channel) {
        case 'rebuildOutputJSON':
          return { success: true, mock: true, message: 'Feature not available in browser' };
        case 'getOutputImages':
          return [];
        case 'getSqliteData':
          throw new Error('SQLite not available in browser environment');
        case 'get-user-data-path':
          return '/mock/user/data/path';
        case 'sqlite-exec':
        case 'sqlite-query':
        case 'sqlite-get':
          throw new Error('SQLite operations require desktop app');
        default:
          console.warn(`[Browser Mock] No mock handler for channel: ${channel}`);
          return null;
      }
    },
    
    // Trigger mock events (for testing)
    _triggerMockEvent: (channel, ...args) => {
      if (mockHandlers.has(channel)) {
        mockHandlers.get(channel).forEach(handler => {
          handler(null, ...args);
        });
      }
    }
  };
};

// Get IPC renderer - either real or mock
const getIpcRenderer = () => {
  const env = getEnvironment();
  
  if (env.hasIPC) {
    try {
      const { ipcRenderer } = window.require('electron');
      return ipcRenderer;
    } catch (error) {
      console.error('Failed to load Electron IPC:', error);
      return createMockIpcRenderer();
    }
  }
  
  return createMockIpcRenderer();
};

// Get shell - either real or mock
const getShell = () => {
  const env = getEnvironment();
  
  if (env.hasShell) {
    try {
      const { shell } = window.require('electron');
      return shell;
    } catch (error) {
      console.error('Failed to load Electron shell:', error);
    }
  }
  
  // Mock shell for browser
  return {
    openExternal: (url) => {
      console.log(`[Browser Mock] Opening external URL: ${url}`);
      window.open(url, '_blank', 'noopener,noreferrer');
      return Promise.resolve();
    },
    showItemInFolder: (path) => {
      console.warn('[Browser Mock] Cannot show item in folder - Electron required');
      return Promise.resolve();
    },
    openPath: (path) => {
      console.warn('[Browser Mock] Cannot open path - Electron required');
      return Promise.resolve();
    }
  };
};

// Export utilities
export const electronBridge = {
  // Environment detection
  getEnvironment,
  isElectron: getEnvironment().isElectron,
  isWeb: getEnvironment().isWeb,
  features: getEnvironment().features,
  
  // Core services
  ipcRenderer: getIpcRenderer(),
  shell: getShell(),
  
  // Utility methods with environment awareness
  log: (message, ...args) => {
    const env = getEnvironment();
    if (env.features.logging) {
      if (env.hasIPC) {
        getIpcRenderer().send('pwa-log', message, ...args);
      } else {
        console.log(`[PWA Log] ${message}`, ...args);
      }
    }
  },
  
  // File system operations (will fail gracefully in browser)
  openDirectory: async () => {
    const env = getEnvironment();
    if (env.features.directorySelection) {
      return new Promise((resolve) => {
        const ipc = getIpcRenderer();
        ipc.once('selectedDirectory', (event, path) => {
          resolve({ canceled: false, path });
        });
        ipc.send('openDirectory');
      });
    } else {
      console.warn('[Browser] Directory selection not available - Electron required');
      return { canceled: true, path: null, error: 'Not available in browser' };
    }
  },
  
  // Upload operations
  upload: async (base64, directory, metadata) => {
    const env = getEnvironment();
    if (env.features.localFileUpload) {
      return getIpcRenderer().send('upload', base64, directory, metadata);
    } else {
      console.warn('[Browser] Local file upload not available - Electron required');
      return Promise.resolve({ mock: true, success: false, error: 'Not available in browser' });
    }
  },
  
  // Image operations
  updateImage: async (imageData) => {
    const env = getEnvironment();
    if (env.features.imageMetadataEdit) {
      return getIpcRenderer().send('update-image', imageData);
    } else {
      console.warn('[Browser] Image metadata update not available - Electron required');
      return Promise.resolve({ success: false, error: 'Not available in browser' });
    }
  },
  
  deleteImage: async (imageData) => {
    const env = getEnvironment();
    if (env.features.fileOperations) {
      return getIpcRenderer().send('delete-image', imageData);
    } else {
      console.warn('[Browser] Image deletion not available - Electron required');
      return Promise.resolve({ success: false, error: 'Not available in browser' });
    }
  },
  
  // ComfyUI operations
  checkComfyUI: async () => {
    const env = getEnvironment();
    if (env.features.comfyUIIntegration) {
      return new Promise((resolve) => {
        const ipc = getIpcRenderer();
        ipc.once('comfyUI_check_result', (event, result) => {
          resolve(result);
        });
        ipc.send('check-comfyui');
      });
    } else {
      console.warn('[Browser] ComfyUI integration not available - Electron required');
      return { available: false, error: 'Not available in browser' };
    }
  },
  
  // Auth operations
  authenticateWithGoogle: async (authUrl) => {
    const env = getEnvironment();
    if (env.features.nativeAuth) {
      return getIpcRenderer().send('google_auth', authUrl);
    } else {
      console.warn('[Browser] Native auth not available - use web OAuth flow');
      // In browser, redirect to auth URL
      window.location.href = authUrl;
      return Promise.resolve({ redirected: true });
    }
  },
  
  // Check specific feature availability
  canUseFeature: (feature) => {
    const env = getEnvironment();
    return env.features[feature] || false;
  },
  
  // Get platform-specific info
  getPlatformInfo: () => {
    const env = getEnvironment();
    return {
      platform: env.platform,
      isElectron: env.isElectron,
      isWeb: env.isWeb,
      isDevelopment: env.isDevelopment,
      capabilities: Object.keys(env.features).filter(f => env.features[f])
    };
  }
};

// For backward compatibility, export individual items
export const isElectronEnv = getEnvironment().isElectron;
export const ipcRenderer = getIpcRenderer();
export default electronBridge;